<script setup>
import { ref, onMounted, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import { useMessageContext } from '../provider.js';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});
const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();
const showGallery = ref(false);

const { filteredCurrentChatAttachments } = useMessageContext();

onMounted(() => {
  if (props.attachment.dataUrl) loadWithRetry(props.attachment.dataUrl);
});

// When the attachment URL is updated (e.g. via ActionCable message_updated
// after a delayed S3 upload finishes), retry loading the new URL.
watch(
  () => props.attachment.dataUrl,
  newUrl => {
    if (newUrl) loadWithRetry(newUrl);
  }
);

const handleError = () => {
  hasError.value = true;
};
</script>

<template>
  <div
    class="size-[72px] overflow-hidden contain-content rounded-xl cursor-pointer"
    @click="showGallery = true"
  >
    <div
      v-if="hasError"
      class="flex flex-col items-center justify-center gap-1 text-xs text-center rounded-lg size-full bg-n-alpha-1 text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </div>
    <img
      v-else-if="isLoaded"
      class="object-cover w-full h-full skip-context-menu"
      :src="attachment.dataUrl"
    />
    <div v-else class="size-full bg-n-alpha-1 animate-pulse" />
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
